import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';

// Hooks
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import useClickOutside from '../../Hooks/useClickOutside.js';

// Components
import { Modal } from '../Modal/modal-component';
import { InputComponent } from '../Inputs/input-component.js';

// Entities & Data
import { ProductStatus } from '../../Entities/Product.ts';
import { getSearchData } from '../../Public/search-data.js';
import { getServicesData } from '../../Public/services-data.js';
import { getBlogsData } from '../../Public/blog-data.js';
import MediaComponent from '../Media/media-component.js';

const SearchWithModal = ({ iconOnly = false, mobileBar = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef(null);
    const [query, setQuery] = useState('');
    const [animateResults, setAnimateResults] = useState(false);
    const [staticData, setStaticData] = useState({ services: [], projects: [], categories: [], blogs: [] });
    
    const [suggestionIndex, setSuggestionIndex] = useState(0);

    const handleRedirect = useHandleRedirect();
    const allProducts = useSelector((state) => Array.isArray(state.cart?.products) ? state.cart.products : []);
    const { language, dir, categories } = useSelector((state) => state.systemData || { language: 'en', dir: 'ltr' });
    const txt = getSearchData(language);

    // ─── PRIORITY SETTINGS ───
    const TYPE_PRIORITY = {
        category: 1,
        service: 2,
        blog: 3,
        product: 4,
        project: 5,
    };

    const typeLabels = txt.typeLabels;

    useClickOutside(contentRef, () => { if (isOpen) handleClose(); });

    useEffect(() => {
        if (isOpen) {
            const fetchData = async () => {
                try {
                    const [sData, bData] = await Promise.all([getServicesData(), getBlogsData()]);
                    setStaticData({ services: sData || [], blogs: bData || [], categories: categories || [] });
                } catch (err) { console.error("Error loading search data", err); }
            };
            fetchData();
        }
    }, [isOpen, categories]);

    // 1. COMBINE DATA
    const combinedData = useMemo(() => {
        let results = [];

        // Map Categories
        if (staticData.categories) {
            staticData.categories.forEach(c => {
                const searchString = `${c.name || ''} ${c.description || ''} category`.toLowerCase();
                results.push({
                    id: c.id,
                    title: c.name,
                    description: c.description,
                    image: c.image || c.media,
                    type: 'category',
                    link: `/categories/${c.slug}`,
                    searchString
                });
            });
        }

        // Map Products
        if (Array.isArray(allProducts)) {
            allProducts.filter(p => p?.status !== ProductStatus.DISABLED && p.showInStore !== false).forEach(p => {
                const searchString = `${p.name || ''} ${p.sku || ''} ${p.category?.name || ''}`.toLowerCase();
                results.push({
                    id: p.id,
                    sku: p.sku || '',
                    title: p.name || 'Untitled Product',
                    description: p.description || '',
                    image: p.image,
                    type: 'product',
                    link: `/shop/${p.handle || p.id}`,
                    searchString
                });
            });
        }

        // Map Blogs
        if (Array.isArray(staticData.blogs)) {
            staticData.blogs.forEach(b => {
                const searchString = `${b.title || ''} ${b.excerpt || ''} ${b.subtitle || ''} ${b.category || ''}`.toLowerCase();
                results.push({
                    id: b.id || b.slug,
                    title: b.title,
                    description: b.excerpt || b.subtitle,
                    image: b.heroImage || b.image || b.media,
                    type: 'blog',
                    link: `/blog/${b.slug}`,
                    searchString,
                });
            });
        }

        return results;
    }, [allProducts, staticData]);

    // ─── CATEGORY-FOCUSED SUGGESTIONS ───
    const typingSuggestions = useMemo(() => {
        const cats = staticData.categories.map(c => `${txt.exploreSuggestion} ${c.name}...`);
        return cats.length > 0 ? cats : txt.fallbackSuggestions;
    }, [staticData.categories]);

    useEffect(() => {
        if (!isOpen || query) return;
        const interval = setInterval(() => {
            setSuggestionIndex((prev) => (prev + 1) % typingSuggestions.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [isOpen, query, typingSuggestions]);

    // 2. FILTER & SORT (Category First)
    const filteredResults = useMemo(() => {
        if (!query.trim()) return [];
        const lower = query.toLowerCase();

        return combinedData
            .filter(item => item.searchString?.includes(lower))
            .sort((a, b) => TYPE_PRIORITY[a.type] - TYPE_PRIORITY[b.type]) // Force sorting by type priority
            .slice(0, 10); // Show top 10 results
    }, [combinedData, query]);

    useEffect(() => {
        setAnimateResults(false);
        const id = requestAnimationFrame(() => setAnimateResults(true));
        return () => cancelAnimationFrame(id);
    }, [query]);

    const handleOpen = () => { setIsOpen(true); };
    const handleClose = () => { setQuery(''); setIsOpen(false); };
    const handleResultClick = (link) => { setQuery(''); setIsOpen(false); handleRedirect(link); };

    const highlightMatch = (text, search) => {
        if (!text || !search?.trim()) return text;
        const index = text.toLowerCase().indexOf(search.toLowerCase());
        if (index === -1) return text;
        return (
            <>
                {text.slice(0, index)}
                <span className="font-bold text-primary-600 dark:text-primary-400">
                    {text.slice(index, index + search.length)}
                </span>
                {text.slice(index + search.length)}
            </>
        );
    };

    const getTypeBadgeClass = (type) => {
        switch (type) {
            case 'category': return 'bg-primary-950 text-white border border-primary-800'; // Categories stand out
            case 'product': return 'bg-gray-100 text-gray-800 border border-gray-200';
            default: return 'bg-amber-100 text-amber-800 border border-amber-200';
        }
    };

    return (
        <>
            {/* --- ICON-ONLY TRIGGER (mobile top navbar) --- */}
            {iconOnly && (
                <button
                    onClick={handleOpen}
                    className="p-2 text-primary-500 active:scale-90 transition-transform"
                    aria-label={txt.triggerAria}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </button>
            )}

            {/* --- MOBILE FULL-WIDTH BAR TRIGGER (mobile search strip) --- */}
            {mobileBar && (
                <div
                    onClick={handleOpen}
                    className="flex w-full items-center bg-gray-100 rounded-xl overflow-hidden cursor-text active:scale-[0.99] transition-transform"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 ml-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <div className="flex-1 px-3 py-3 text-sm text-gray-400 font-medium select-none">
                        {txt.mobilePlaceholder}
                    </div>
                </div>
            )}

            {/* --- DESKTOP BAR TRIGGER --- */}
            {!iconOnly && !mobileBar && (
                <div
                    onClick={handleOpen}
                    className="hidden lg:flex flex-1 max-w-2xl mx-8 items-center border border-gray-300 rounded-[2px] overflow-hidden hover:border-primary-500 cursor-text transition-all bg-white group"
                >
                    <div className="w-full px-4 py-2.5 text-sm text-gray-400 font-medium select-none">
                        {txt.desktopPlaceholder}
                    </div>
                    <button className="bg-primary-500 group-hover:bg-primary-600 px-6 py-2.5 flex items-center justify-center transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>
            )}

            {isOpen && (
            <Modal expand={true} popUp>
                <div
                    ref={contentRef}
                    dir={dir}
                    className="relative w-screen h-screen lg:w-full lg:max-w-4xl lg:h-[80vh] lg:min-h-[650px] p-4 lg:rounded-3xl bg-white dark:bg-[#0c0c0c] shadow-2xl border-t-[6px] border-primary-600 flex flex-col overflow-hidden transition-all duration-500"
                >
                    {/* Header */}
                    <div className="flex-shrink-0 flex items-center justify-between px-6 py-5 lg:px-10 lg:py-6 border-b rounded-t-3xl border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-[#111]">
                        <div className="flex items-center gap-3 lg:gap-5">
                            <div className="text-start">
                                <p 
                                    className="text-2xl lg:text-3xl font-bold text-primary-950 dark:text-white uppercase  italic leading-none"
                                    
                                >
                                    {txt.title}
                                </p>
                                <p className="text-[10px] lg:text-xs text-gray-400 font-bold uppercase tracking-[0.2em] mt-1">
                                    {txt.subtitle}
                                </p>
                            </div>
                        </div>
                        <button onClick={handleClose} className="p-2 text-gray-400 hover:text-primary-600 transition-transform hover:rotate-90">
                            <svg className="w-6 h-6 lg:w-8 lg:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Input Area */}
                    <div className="flex-shrink-0 px-6 py-4 lg:px-10 lg:py-6 bg-white dark:bg-[#0c0c0c]">
                        <div className="relative flex items-center rounded-3xl border-2 border-primary-500 dark:border-primary-600 bg-white dark:bg-neutral-900 px-4 py-3 lg:px-6 lg:py-4 transition-all focus-within:ring-4 focus-within:ring-primary-100">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary-500 z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            
                            <div className="relative flex-1 h-8 lg:h-10 ml-3 lg:ml-5">
                                <AnimatePresence mode="wait">
                                    {!query && (
                                        <motion.div
                                            key={suggestionIndex}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.4 }}
                                            className="absolute inset-0 flex items-center pointer-events-none"
                                        >
                                            <span className="text-lg lg:text-xl font-bold text-gray-300 italic">
                                                {typingSuggestions[suggestionIndex]}
                                            </span>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <InputComponent
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder=""
                                    className="absolute inset-0 w-full h-full bg-transparent outline-none border-none shadow-none text-lg lg:text-xl font-bold text-start text-primary-950 dark:text-white z-20"
                                    autoFocus={isOpen}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Area */}
                    <div className="flex-1 overflow-y-auto px-4 lg:px-10 pb-6 lg:pb-10 custom-scrollbar bg-white dark:bg-[#0c0c0c]">
                        {query.trim() && filteredResults.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center p-10 opacity-40">
                                <p className="text-center text-gray-500 font-bold uppercase tracking-widest italic">
                                    {txt.noResults}
                                </p>
                            </div>
                        ) : (
                            <ul className="space-y-3 lg:space-y-4">
                                {filteredResults.map((item) => (
                                    <li
                                        key={`${item.type}-${item.id}`}
                                        className={`flex items-center gap-4 lg:gap-6 px-4 py-3 lg:px-6 lg:py-4 rounded-3xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 cursor-pointer hover:border-primary-500 hover:shadow-md transition-all transform ${animateResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                                        onClick={() => handleResultClick(item.link)}
                                    >
                                        <div className="flex-shrink-0 w-14 h-14 lg:w-20 lg:h-20 overflow-hidden bg-gray-50 dark:bg-black border border-gray-100 rounded-2xl">
                                            <MediaComponent
                                                src={item.image}
                                                className="w-full h-full"
                                                wrapperClassName="w-full h-full flex items-center justify-center"
                                                objectFit="fit"
                                                width={500}
                                                quality={80} />
                                        </div>

                                        <div className="flex-1 text-start min-w-0">
                                            <div className="flex items-center gap-3 mb-1.5">
                                                <span className={`text-[10px] lg:text-xs px-2.5 py-1 rounded-3xl font-bold uppercase tracking-widest inline-block ${getTypeBadgeClass(item.type)}`}>
                                                    {typeLabels[item.type]}
                                                </span>
                                            </div>
                                            <p className={`text-base lg:text-lg font-bold text-primary-950 dark:text-white truncate ${item.type === 'category' ? 'italic' : ''}`}>
                                                {highlightMatch(item.title, query)}
                                            </p>
                                        </div>

                                        <svg className="w-5 h-5 lg:w-6 lg:h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path d="M9 5l7 7-7 7" />
                                        </svg>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </Modal>
            )}
        </>
    );
};

export default SearchWithModal;